/**
 * /settings/mail — user-facing mail settings.
 *
 * For now: the External Clients (IMAP/SMTP) app-password surface.
 * Will grow other panels (signature, away message, forwarding rules)
 * as those land.
 *
 * The component is `ExternalClientsSettings` from `@postopia/mail-ui`
 * — same component MyVigator's EMS uses. This file wires the
 * product-specific data source (calls /api/v1/mail/me/app-passwords
 * Pages Functions) + server config (mx.postopia.com hostnames).
 */

import { createFileRoute } from '@tanstack/react-router';
import {
  ExternalClientsSettings,
  type AppPasswordSummary,
  type CreateAppPasswordSuccess,
  type CreateAppPasswordFailure,
  type RevokeAppPasswordResult,
  type ExternalClientsDataSource,
  type ExternalClientsServerConfig,
  type AppPasswordScope,
} from '@postopia/mail-ui';
import { useQuery } from '@tanstack/react-query';
import { AdminShell } from '../components/AdminShell';
import { mailApi } from '../lib/mailApi';

export const Route = createFileRoute('/settings/mail')({
  component: SettingsMailRoute,
});

// ────────────────────────────────────────────────────────────────────
// Postopia data source — wraps the Pages Function endpoints.
// ────────────────────────────────────────────────────────────────────

const dataSource: ExternalClientsDataSource = {
  async listAppPasswords() {
    const r = await fetch('/api/v1/mail/me/app-passwords', {
      credentials: 'include',
    });
    if (!r.ok) throw new Error(`list failed: ${r.status}`);
    const body = (await r.json()) as
      | { ok: true; app_passwords: AppPasswordSummary[] }
      | { ok: false; code: string };
    if (!body.ok) throw new Error(body.code);
    return body.app_passwords;
  },
  async createAppPassword(input: {
    label: string;
    scope: AppPasswordScope;
  }): Promise<CreateAppPasswordSuccess | CreateAppPasswordFailure> {
    const r = await fetch('/api/v1/mail/me/app-passwords', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!r.ok) {
      try {
        const j = (await r.json()) as { code?: string; message?: string };
        return {
          ok: false,
          code:
            j.code === 'invalid_label' ||
            j.code === 'invalid_scope' ||
            j.code === 'too_many'
              ? j.code
              : 'network',
          message: j.message,
        };
      } catch {
        return { ok: false, code: 'network', message: `${r.status}` };
      }
    }
    return (await r.json()) as CreateAppPasswordSuccess;
  },
  async revokeAppPassword(id: string): Promise<RevokeAppPasswordResult> {
    const r = await fetch(`/api/v1/mail/me/app-passwords/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!r.ok) {
      try {
        const j = (await r.json()) as { code?: string; message?: string };
        return {
          ok: false,
          code:
            j.code === 'not_found' || j.code === 'already_revoked'
              ? j.code
              : 'network',
          message: j.message,
        };
      } catch {
        return { ok: false, code: 'network', message: `${r.status}` };
      }
    }
    return (await r.json()) as { ok: true; revoked_at: string };
  },
};

// ────────────────────────────────────────────────────────────────────
// Server config — Postopia-specific. Lives here, not in the shared
// component, so MyV can supply its own hostnames without ever knowing
// about ours.
// ────────────────────────────────────────────────────────────────────

const SERVER_CONFIG_BASE = {
  hostname: 'mx.postopia.com',
  imap: { port: 993, security: 'SSL/TLS' as const },
  smtp: { port: 465, security: 'SSL/TLS' as const },
};

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────

function SettingsMailRoute() {
  // Postopia staff: CF Access email IS the platform email (Zitadel
  // emits the same `sam@postopia.com` for both SSO and the mailbox).
  // MyV's settings page will source this from its own session helper
  // since their candidates' SSO email differs from their mailbox.
  const { data: me } = useQuery({
    queryKey: ['mail', 'me'],
    queryFn: () => mailApi.me(),
  });
  const email = me?.actor?.email;
  const serverConfig: ExternalClientsServerConfig | null = email
    ? { ...SERVER_CONFIG_BASE, email }
    : null;

  return (
    <AdminShell mode="feature" featureName="Mail settings">
      {serverConfig ? (
        <div style={{ maxWidth: '680px', margin: '0 auto', padding: '20px' }}>
          <ExternalClientsSettings
            dataSource={dataSource}
            serverConfig={serverConfig}
          />
        </div>
      ) : (
        <div
          style={{
            maxWidth: '680px',
            margin: '40px auto',
            padding: '20px',
            color: 'var(--c-fg-secondary)',
          }}
        >
          No mailbox is provisioned for your account yet. Ask an admin
          to provision one (or open the Staff page to self-provision).
        </div>
      )}
    </AdminShell>
  );
}
